import { useQuery } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { screenTitle } from '@/config/navigation';
import { crumbForPath, screenIdForPath } from '@/lib/active-screen';
import { useAuth } from '@/lib/auth/auth-context';
import { scopeCountsQueryOptions } from '@/lib/scope-counts-query';

import { Sidebar, SidebarContent } from './Sidebar';
import { TopBar } from './TopBar';

interface AppShellProps {
  readonly children: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  // Selecting just the pathname keeps the shell out of re-renders caused by
  // search-param changes, which every filtered table will produce.
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const [isNavOpen, setNavOpen] = useState(false);

  const { api } = useAuth();
  /*
   * Fetched here rather than per screen: the sidebar needs three of these on
   * every screen anyway, and the list screens' pills then read the same cached
   * answer instead of each asking again. A failure is silent — a badge is a
   * nicety, and an error banner over the whole console because a count query
   * timed out would be worse than no badge.
   */
  const counts = useQuery(scopeCountsQueryOptions(api));
  const badges =
    counts.data === undefined
      ? undefined
      : {
          foodsToReview: counts.data.foods.review,
          openFoodRequests: counts.data.foodRequests.open,
          openFeedback: counts.data.feedback.open,
          failedPlanRuns: counts.data.planRuns.failedInWindow,
          gymCatalogWaiting:
            counts.data.gymCatalog.foods + counts.data.gymCatalog.exercises + counts.data.gymCatalog.reports,
          openExerciseRequests: counts.data.exerciseRequests.open,
        };

  const screenId = screenIdForPath(pathname);
  const title = screenId === null ? 'Not found' : screenTitle(screenId);
  const crumb = crumbForPath(pathname);

  return (
    <div className="flex h-dvh overflow-hidden bg-background">
      <Sidebar activeScreenId={screenId} {...(badges === undefined ? {} : { badges })} />

      {/* Below `lg` the rail is a drawer. Same contents, same active state. */}
      <Sheet open={isNavOpen} onOpenChange={setNavOpen}>
        <SheetContent
          side="left"
          className="w-[272px] border-r border-sidebar-border bg-sidebar p-0 text-sidebar-foreground"
        >
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {/*
            Closed from the link itself rather than by watching the pathname.
            An effect that calls `setState` on every navigation re-renders the
            whole shell a second time for a panel that is usually already shut.
          */}
          <SidebarContent
            activeScreenId={screenId}
            {...(badges === undefined ? {} : { badges })}
            onNavigate={() => {
              setNavOpen(false);
            }}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          title={title}
          crumb={crumb}
          onOpenNav={() => {
            setNavOpen(true);
          }}
        />

        {/*
          `relative` is load-bearing, not cosmetic. `sr-only` hides things by
          absolutely positioning them, and an absolutely positioned element
          with no positioned ancestor resolves against the viewport instead of
          its scroll container. A visually hidden node far down a long screen
          therefore sat far down the *document*, giving the page a scrollbar of
          its own and scrolling the sidebar along with it. Positioning `main`
          keeps every such node inside the thing that is supposed to scroll.
        */}
        <main className="relative flex-1 overflow-y-auto p-4 sm:p-[22px]">
          {/*
            The cap is per screen in the design — a form wants 820px and the
            catalog wants 1300 — so the shell only stops a table from running
            the full width of an ultrawide monitor, and a screen that wants to
            be narrower says so itself.
          */}
          <div className="mx-auto flex max-w-[1300px] flex-col gap-3.5">{children}</div>
        </main>
      </div>
    </div>
  );
}
